import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { cn } from "@/lib/utils";
import React from "react";
import { Video } from "lucide-react";

export function AgentVidAvatar({ className, style }: { className?: string; style?: React.CSSProperties }) {
  // Track raw mouse position relative to center
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Apply a spring for a bouncy, shaky, responsive feel
  const springConfig = { damping: 8, stiffness: 250, mass: 0.5 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);

  // Create aggressive 3D rotation effects based on smooth mouse position
  const rotateX = useTransform(smoothY, [-100, 100], [30, -30]);
  const rotateY = useTransform(smoothX, [-100, 100], [-30, 30]);
  
  // Create translation so the avatar leans towards the cursor
  const translateX = useTransform(smoothX, [-100, 100], [-10, 10]);
  const translateY = useTransform(smoothY, [-100, 100], [-10, 10]);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    // Calculate distance from center, capped to a range of -100 to 100
    const dx = Math.max(-100, Math.min(100, event.clientX - centerX));
    const dy = Math.max(-100, Math.min(100, event.clientY - centerY));
    
    mouseX.set(dx);
    mouseY.set(dy);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <motion.div 
      className={cn("relative w-full h-full flex items-center justify-center group", className)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ perspective: 1000, ...style }}
    >
      {/* 
        Outer wrapper handles the dynamic following/shaking (x, y, rotateX, rotateY). 
      */}
      <motion.div
        className="relative w-full h-full preserve-3d cursor-pointer"
        style={{
          rotateX,
          rotateY,
          x: translateX,
          y: translateY,
          transformStyle: "preserve-3d",
        }}
      >
        {/* 
          Inner wrapper handles the idle hovering animation so it doesn't conflict 
          with the spring-driven y-translation.
        */}
        <motion.div
          className="relative w-full h-full"
          style={{ transformStyle: "preserve-3d" }}
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          {/* Core Head Structure - Dark Metallic/Gunmetal */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-zinc-600 via-zinc-800 to-black shadow-[0_0_15px_rgba(0,0,0,0.8)] overflow-hidden">
            {/* Top highlight / metallic sheen */}
            <div className="absolute top-0 inset-x-0 h-[45%] bg-gradient-to-b from-white/20 to-transparent opacity-80 rounded-t-2xl" />
            
            {/* Inner robotic details */}
            <div className="absolute inset-[3px] rounded-[13px] border border-zinc-500/30 bg-zinc-900/90 backdrop-blur-sm" />
            <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-zinc-400/60" />
            
            {/* Chin plate */}
            <div className="absolute bottom-0 inset-x-0 h-1/3 bg-gradient-to-t from-black/90 to-transparent rounded-b-2xl" />
            {/* Alien Mouth */}
            <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 w-12 h-1.5 rounded-full bg-black shadow-[inset_0_2px_4px_rgba(0,0,0,1)] border-b border-zinc-700/50" />
            <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 w-10 h-0.5 rounded-full bg-zinc-500/80" />
            
            {/* Side plates / Ear modules */}
            <div className="absolute top-1/2 left-0 -translate-y-1/2 w-1 h-4 bg-zinc-400/80 rounded-r-sm shadow-[0_0_8px_rgba(161,161,170,0.8)]" />
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-1 h-4 bg-zinc-400/80 rounded-l-sm shadow-[0_0_8px_rgba(161,161,170,0.8)]" />
            
            {/* Antenna */}
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1 h-2 bg-zinc-500 rounded-t-sm" />
            <motion.div 
              className="absolute -top-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-[#3b82f6] rounded-full shadow-[0_0_8px_rgba(59,130,246,1)]"
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>

          {/* Floating Visor (Eyes) - Parallax pushed further out for deeper 3D tracking */}
          <motion.div 
            className="absolute inset-0 flex items-center justify-center"
            style={{ transform: "translateZ(20px)" }} // Increased parallax
          >
            {/* Intense background glow behind the visor */}
            <div className="absolute inset-x-0 top-[30%] h-[40%] bg-blue-500/20 rounded-lg blur-md" />
            <div className="absolute inset-x-1 top-[35%] h-[30%] bg-black/60 rounded-lg blur-[2px]" />
            
            {/* The Icon as glowing eyes */}
            <motion.div
              className="w-[80%] h-auto relative z-10 flex items-center justify-between px-2 text-[#3b82f6] scale-[1.3]"
              style={{ filter: "drop-shadow(0 0 10px rgba(59,130,246,1)) drop-shadow(0 0 20px rgba(59,130,246,0.8))" }}
              initial={{ opacity: 0.9 }}
              animate={{ opacity: [0.85, 1, 0.85] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <Video className="w-[40%] h-auto transform scale-x-[-1]" strokeWidth={2} />
              <Video className="w-[40%] h-auto" strokeWidth={2} />
            </motion.div>
          </motion.div>
          
          {/* Animated scanning line - enhanced to match new eyes */}
          <motion.div 
            className="absolute inset-x-0 h-[2px] bg-[#3b82f6]/60 blur-[1px] shadow-[0_0_8px_rgba(59,130,246,0.8)]"
            animate={{ top: ["15%", "85%", "15%"] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
            style={{ transform: "translateZ(5px)" }}
          />
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
